import { isInitializing } from '@va/dashboard/selectors/app';
import { activeWebsiteSelectors } from '@va/dashboard/selectors/core';
import { FeatureCounterContextProvider } from '@va/dashboard/shared/feature-counter';
import {
  getLayoutClassName,
  LayoutProps,
  LayoutStickyHeaderWrapper,
  useScrollToTopEffect,
} from '@va/dashboard/shared/ui-layout';
import { isAccountSettingsPageSelected } from '@va/standalone/shared/helpers';
import { LoaderBar } from '@va/ui/design-system';
import { PageHeaderContextProvider, RefreshContextProvider } from '@va/util/components';
import classNames from 'classnames';
import { PropsWithChildren, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import AaasModals from '../components/modals/AaasModals';
import { LayoutPreHeader } from './LayoutPreHeader';
import { SidebarWrapper } from './SidebarWrapper';
import TopBar from './TopBar';
import useTopBarVisibility from './useTopBarVisibility';

export default function Layout({ children, hasBackButton = false }: PropsWithChildren<LayoutProps>) {
  useScrollToTopEffect();
  const initializing = useSelector(isInitializing);

  useTopBarVisibility();
  const { pathname } = useLocation();

  const showSidebar = useMemo(() => !isAccountSettingsPageSelected(pathname), [pathname]);

  const contentClassName = useMemo(() => getLayoutClassName(showSidebar), [showSidebar]);

  const isActiveWebsiteChangeInProgress = useSelector(activeWebsiteSelectors.selectIsLoading);

  if (initializing) {
    return <LoaderBar />;
  }

  return (
    <>
      <TopBar hasBackButton={hasBackButton} />
      {isActiveWebsiteChangeInProgress ? (
        <LoaderBar />
      ) : (
        <FeatureCounterContextProvider features={{ apiKeys: false }}>
          <>
            <SidebarWrapper />
            <RefreshContextProvider>
              <PageHeaderContextProvider>
                <div className={classNames('flex relative min-h-full pt-[var(--top-bar-height)]')}>
                  <div className={contentClassName}>
                    <LayoutPreHeader />
                    <LayoutStickyHeaderWrapper />
                    <AaasModals />
                    {children}
                  </div>
                </div>
              </PageHeaderContextProvider>
            </RefreshContextProvider>
          </>
        </FeatureCounterContextProvider>
      )}
    </>
  );
}
